import * as Sentry from "@sentry/react"
import { getAppEnvironment } from "@/config/environment"
import type { StoredUser } from "@/lib/auth"

// Browser-only error reporting (static-export SPA, no server runtime). Errors
// only — no tracing or Session Replay (Replay would capture customer PII).

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"])

export function isLocalhost(hostname?: string): boolean {
  if (!hostname) return false
  return LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith(".local")
}

export interface SentryRuntimeEnv {
  dsn?: string
  environment?: string
  release?: string
  hostname?: string
  nodeEnv?: string
}

export interface ResolvedSentryConfig {
  enabled: boolean
  dsn?: string
  environment: string
  release?: string
}

// Pure so the gating rules can be unit-tested without the global SDK.
export function resolveSentryConfig(env: SentryRuntimeEnv): ResolvedSentryConfig {
  const isLocal = isLocalhost(env.hostname) || env.nodeEnv === "development"
  const enabled = Boolean(env.dsn) && !isLocal
  return {
    enabled,
    dsn: env.dsn,
    environment: env.environment ?? "unknown",
    release: env.release,
  }
}

let initialized = false

export function initSentry(): void {
  if (initialized) return
  if (typeof window === "undefined") return

  const config = resolveSentryConfig({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment: getAppEnvironment(),
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    hostname: window.location.hostname,
    nodeEnv: process.env.NODE_ENV,
  })

  if (!config.enabled || !config.dsn) return

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    sendDefaultPii: false,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Non-Error promise rejection captured",
    ],
  })

  initialized = true
}

// Errors swallowed into a toast never reach Sentry on their own. Report the ones
// worth alerting on — without file names, which carry customer PII.
export function captureHandledError(
  error: unknown,
  context: { flow: string } & Record<string, unknown>
): void {
  if (typeof window === "undefined") return
  const { flow, ...extra } = context
  Sentry.captureException(error, { tags: { flow }, extra })
}

// No-op when the SDK was never initialized (localhost).
export function setSentryUser(user: StoredUser | null): void {
  if (typeof window === "undefined") return
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.fullName ?? undefined,
  })
}
